import { useState, useEffect } from "react";

interface AgeVerificationProps {
  onVerified: () => void;
}

export default function AgeVerification({ onVerified }: AgeVerificationProps) {
  const [visible, setVisible] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    const localVerified = localStorage.getItem("age_verified_permanent") === "true";
    const sessionVerified = sessionStorage.getItem("age_verified") === "true";
    if (localVerified || sessionVerified) {
      setVisible(false);
      onVerified();
    } else {
      setVisible(true);
    }
  }, [onVerified]);

  // 表示中はbodyのスクロールをロック
  useEffect(() => {
    if (visible) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      document.body.style.overflowY = "";
      document.documentElement.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.overflowY = "";
      document.documentElement.style.overflow = "";
    };
  }, [visible]);

  const handleYes = () => {
    if (dontShowAgain) {
      localStorage.setItem("age_verified_permanent", "true");
    }
    sessionStorage.setItem("age_verified", "true");
    setVisible(false);
    onVerified();
  };

  const handleNo = () => {
    window.location.href = "https://abehiroshi.la.coocan.jp/";
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #fdfcfb 0%, #f9f6f2 40%, #fdfcfb 70%, #f5f2ee 100%)"
      }}
    >
      {/* 背景装飾：薄い円形グラデーション */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[50vw] h-[50vw] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(212,196,180,0.18) 0%, transparent 70%)" }}
        />
        <div className="absolute bottom-[-10%] right-[-10%] w-[45vw] h-[45vw] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(200,185,168,0.15) 0%, transparent 70%)" }}
        />
        <div className="absolute top-[30%] right-[5%] w-[25vw] h-[25vw] rounded-full"
          style={{ background: "radial-gradient(circle, rgba(220,210,198,0.12) 0%, transparent 70%)" }}
        />
      </div>

      {/* 四隅の装飾ライン */}
      <div className="absolute top-6 left-6 w-16 h-16 pointer-events-none">
        <div className="absolute top-0 left-0 w-full h-px bg-stone-300/60" />
        <div className="absolute top-0 left-0 w-px h-full bg-stone-300/60" />
      </div>
      <div className="absolute top-6 right-6 w-16 h-16 pointer-events-none">
        <div className="absolute top-0 right-0 w-full h-px bg-stone-300/60" />
        <div className="absolute top-0 right-0 w-px h-full bg-stone-300/60" />
      </div>
      <div className="absolute bottom-6 left-6 w-16 h-16 pointer-events-none">
        <div className="absolute bottom-0 left-0 w-full h-px bg-stone-300/60" />
        <div className="absolute bottom-0 left-0 w-px h-full bg-stone-300/60" />
      </div>
      <div className="absolute bottom-6 right-6 w-16 h-16 pointer-events-none">
        <div className="absolute bottom-0 right-0 w-full h-px bg-stone-300/60" />
        <div className="absolute bottom-0 right-0 w-px h-full bg-stone-300/60" />
      </div>

      {/* 上下の細いボーダーライン */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-stone-300/50 to-transparent pointer-events-none" />
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-stone-300/50 to-transparent pointer-events-none" />

      {/* メインカード */}
      <div className="relative w-full max-w-md mx-4 text-center px-6 sm:px-10 py-14"
        style={{
          background: "rgba(255,255,255,0.75)",
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(210,200,188,0.4)",
        }}
      >
        {/* カード内コーナー装飾 */}
        <div className="absolute top-4 left-4 w-6 h-6 pointer-events-none">
          <div className="absolute top-0 left-0 w-full h-px bg-stone-300/70" />
          <div className="absolute top-0 left-0 w-px h-full bg-stone-300/70" />
        </div>
        <div className="absolute top-4 right-4 w-6 h-6 pointer-events-none">
          <div className="absolute top-0 right-0 w-full h-px bg-stone-300/70" />
          <div className="absolute top-0 right-0 w-px h-full bg-stone-300/70" />
        </div>
        <div className="absolute bottom-4 left-4 w-6 h-6 pointer-events-none">
          <div className="absolute bottom-0 left-0 w-full h-px bg-stone-300/70" />
          <div className="absolute bottom-0 left-0 w-px h-full bg-stone-300/70" />
        </div>
        <div className="absolute bottom-4 right-4 w-6 h-6 pointer-events-none">
          <div className="absolute bottom-0 right-0 w-full h-px bg-stone-300/70" />
          <div className="absolute bottom-0 right-0 w-px h-full bg-stone-300/70" />
        </div>

        <div className="mb-8">
          <p className="text-xs tracking-[0.3em] text-stone-400 uppercase mb-3">Age Verification</p>
          <h1 className="font-en text-2xl font-light tracking-widest text-stone-800 mb-1">
            Mugi Cha Production
          </h1>
          {/* 装飾ライン（ダイヤモンド付き） */}
          <div className="flex items-center justify-center gap-3 my-6">
            <div className="flex-1 max-w-[60px] h-px bg-stone-300/70" />
            <div className="w-1.5 h-1.5 rotate-45 bg-stone-300" />
            <div className="flex-1 max-w-[60px] h-px bg-stone-300/70" />
          </div>
        </div>

        <p className="text-sm text-stone-600 leading-relaxed mb-8 whitespace-nowrap">
          本サイトは<strong className="text-stone-800">18歳以上</strong>を対象としています。
        </p>

        <p className="text-base text-stone-700 font-medium mb-8 tracking-wide">
          あなたは18歳以上ですか？
        </p>

        <div className="flex gap-4 justify-center">
          <button
            onClick={handleYes}
            className="px-10 py-3 bg-stone-800 text-white text-sm tracking-[0.2em] hover:bg-stone-700 transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            YES
          </button>
          <button
            onClick={handleNo}
            className="px-10 py-3 border border-stone-500 text-stone-600 text-sm tracking-[0.2em] hover:bg-stone-50 transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            NO
          </button>
        </div>

        {/* 今後表示しないチェックボックス */}
        <div className="mt-6 flex items-center justify-center gap-2 cursor-pointer select-none"
          onClick={() => setDontShowAgain(!dontShowAgain)}
        >
          <div className={`w-4 h-4 border transition-colors duration-200 flex items-center justify-center ${
            dontShowAgain
              ? "bg-stone-800 border-stone-800"
              : "bg-white border-stone-300"
          }`}>
            {dontShowAgain && (
              <i className="ri-check-line text-white text-xs"></i>
            )}
          </div>
          <span className="text-xs text-stone-600 tracking-wide">
            今後この確認を表示しない
          </span>
        </div>

        <div className="mt-8 text-xs md:text-xs text-[11px] text-stone-400 leading-relaxed space-y-1.5">
          <p>「YES」をクリックすることで18歳以上であることを</p>
          <p>確認したものとみなします。</p>
        </div>
      </div>
    </div>
  );
}